/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { FormattedMessage, useIntl } from "react-intl"
import { PlanInfoBadgeContainer } from "../../../../containers/planinfo/PlanInfoBadgeContainer"
import React from "react"
import {
  getCompletedReimbursementsList,
  getPendingReimbursementsList,
  getReimbursementsRequiringActionList
} from "../ReimbursementListUtils"
import { Legend, ReimbursementsListSection, ReimbursementSummary, TableHeader } from "../SharedReimbursementElements"
import { useSelector } from "react-redux"
import { getCardState } from "#/redux/reducers/userInfo"
import CardStates from "../../../../../utils/CardStates"
import { ExpenseState } from "../../../../../utils/ExpenseState"
import { Badge, Box, Typography, useTheme, Stack } from "@carrotfertility/carotene-core"
import icnTransactionSrc from "./assets/images/icn-transaction.png"
import icnTransaction2xSrc from "./assets/images/icn-transaction-2x.png"
import { TextDirection } from "#/types/Locale"
import { useTextDirection } from "../../../../../services/locale/hooks/useTextDirection"

const EmptyReimbursementsList = () => {
  const isCardEligible = useSelector(getCardState) !== CardStates.INELIGIBLE
  return (
    <div>
      <Box paddingTop={(theme) => theme.tokens.spacing.md} />
      <table className="w-100 collapse">
        <thead>
          <tr>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          {/* This color should be specified using a carotene-core palette color, but that kind of requires refactoring
              this whole table into a carotene-core Table which is going to be nontrivial. See LG-1524. */}
          <tr className="bt bw1" style={{ borderColor: "#b9b9b9" }}>
            <td colSpan={3}>
              <div className="w-100 flex justify-center">
                <div className="flex flex-column items-center" style={{ maxWidth: "360px" }}>
                  <Box
                    padding={(theme) => theme.tokens.spacing.md}
                    borderRadius={(theme) => theme.tokens.borderRadius.round}
                    marginTop={(theme) => theme.tokens.spacing.xxl}
                    height="84px"
                    width="84px"
                    sx={(theme) => ({ backgroundColor: theme.palette.secondary.light })}
                  >
                    <img
                      src={icnTransactionSrc}
                      srcSet={`${icnTransaction2xSrc} 2x`}
                      alt=""
                      height="50px"
                      width="50px"
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    color="tertiary"
                    align="center"
                    marginTop={(theme) => theme.tokens.spacing.lg}
                  >
                    {isCardEligible ? (
                      <FormattedMessage defaultMessage="Your Carrot Card® transactions and reimbursement requests will appear here." />
                    ) : (
                      <FormattedMessage defaultMessage="Your reimbursement requests will appear here." />
                    )}
                  </Typography>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface SingleCategoryReimbursementsListProps {
  reimbursementsList: any[]
  subsidy: any
  onTransactionClick?: (transaction: any) => void
}

export const SingleCategoryReimbursementsList = ({
  reimbursementsList,
  subsidy,
  onTransactionClick
}: SingleCategoryReimbursementsListProps) => {
  const intl = useIntl()
  const theme = useTheme()
  const reimbursementsRequiringAction = getReimbursementsRequiringActionList(reimbursementsList)
  const reimbursementsPending = getPendingReimbursementsList(reimbursementsList)
  const reimbursementsCompleted = getCompletedReimbursementsList(reimbursementsList)
  const textDirection = useTextDirection()

  return (
    <>
      {subsidy.hasUnlimitedBenefit || (
        <>
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xxxl })}>
            <Typography
              variant="h4"
              component="h2"
              color={theme.palette.text.primary}
              paddingBottom={(theme) => theme.tokens.spacing.md}
            >
              <FormattedMessage defaultMessage="Available funds" description="money available to be spent" />
            </Typography>
            <ReimbursementSummary
              subsidy={subsidy}
              reimbursementsCount={reimbursementsList ? reimbursementsList.length : 0}
              showRemainingAnnual={
                !subsidy.hasUnlimitedBenefit && subsidy.yearlyAmounts.max !== subsidy.lifetimeAmounts.max
              }
              showRemainingLifetime={!subsidy.hasUnlimitedBenefit && subsidy.lifetimeAmounts.max !== null}
              // @ts-expect-error TS(2322) FIXME: Type '{ subsidy: any; reimbursementsCount: any; sh... Remove this comment to see the full error message
              paddingTop={(theme) => theme.tokens.spacing.md}
              // @ts-expect-error TS(7006) FIXME: Parameter 'theme' implicitly has an 'any' type.
              paddingBottom={(theme) => theme.tokens.spacing.md}
            />
            <PlanInfoBadgeContainer
              subsidy={subsidy}
              // @ts-expect-error TS(2322) FIXME: Type '{ subsidy: any; categorySupportsDeductibles:... Remove this comment to see the full error message
              categorySupportsDeductibles={true}
              sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.md })}
            />
          </Box>
        </>
      )}
      {reimbursementsList?.length > 0 ? (
        <>
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
            <Typography variant="h6" component="h2" sx={{ fontSize: "20px" }}>
              <FormattedMessage defaultMessage="Activity" description="things that have been done" />
            </Typography>
          </Box>

          <ReimbursementsListSection
            title={
              <Stack
                direction={textDirection === TextDirection.RightToLeft ? "row-reverse" : "row"}
                justifyContent="space-between"
              >
                <Badge
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: textDirection === TextDirection.RightToLeft ? "left" : "right"
                  }}
                  badgeContent={intl.formatNumber(reimbursementsRequiringAction.length)}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge.MuiBadge-anchorOriginTopRight": {
                      insetBlockStart: "50%",
                      transform: "scale(1) translate(26px, -70%)"
                    },
                    "& .MuiBadge-badge.MuiBadge-anchorOriginTopLeft": {
                      insetBlockStart: "50%",
                      transform: "scale(1) translate(-26px, -70%)"
                    }
                  }}
                >
                  <Typography
                    variant="h3"
                    color={(theme) => theme.palette.text.secondary}
                    sx={{ ...theme.typography.chip }}
                  >
                    <FormattedMessage defaultMessage="Needs Attention" />
                  </Typography>
                </Badge>
                <Legend />
              </Stack>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No transactions need attention" })}
            items={reimbursementsRequiringAction}
            showAlert={reimbursementsRequiringAction.some(
              (e) =>
                e.expenseState === ExpenseState.NEEDS_MEMBER_UPLOADS ||
                e.expenseState === ExpenseState.DENIED_RECONCILIATION_REQUESTED ||
                e.reconciliationRequestedExpenseId
            )}
            onTransactionClick={onTransactionClick}
          />
          <ReimbursementsListSection
            title={
              <Typography
                variant="h3"
                color={(theme) => theme.palette.text.secondary}
                sx={{ ...theme.typography.chip }}
              >
                <FormattedMessage defaultMessage="Pending" />
              </Typography>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No pending transactions" })}
            items={reimbursementsPending}
            onTransactionClick={onTransactionClick}
          />
          <ReimbursementsListSection
            title={
              <Typography
                variant="h3"
                color={(theme) => theme.palette.text.secondary}
                sx={{ ...theme.typography.chip }}
              >
                <FormattedMessage defaultMessage="Complete" />
              </Typography>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No completed transactions" })}
            items={reimbursementsCompleted}
            onTransactionClick={onTransactionClick}
          />
        </>
      ) : (
        <EmptyReimbursementsList />
      )}
    </>
  )
}
